import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { manifestReceivingAPI, documentsAPI, adminAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import BulkIdentifierInput from '../../../components/BulkIdentifierInput';
import HoldLineItemModal from '../../../components/modals/HoldLineItemModal';
import CancelLineItemModal from '../../../components/modals/CancelLineItemModal';
import HoldInfoPopover from '../../../components/HoldInfoPopover';
import {
  FiArrowLeft,
  FiPackage,
  FiUpload,
  FiCheck,
  FiX,
  FiPlus,
  FiMinus,
  FiInfo,
  FiPrinter,
  FiPause,
  FiSlash,
  FiCopy,
  FiEdit2,
  FiTrash2
} from 'react-icons/fi';

const getReceivingServiceLabels = (instructions = {}) => {
  const labels = [];
  if (instructions.require_serial_numbers_on_receive) labels.push('Record serial numbers');
  if (instructions.require_mac_addresses_on_receive) labels.push('Record MAC addresses');
  if (instructions.relabel_on_receive) labels.push('Relabel on receipt');
  return labels;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

let _tempIdCounter = 0;
const generateTempId = () => `temp-${Date.now()}-${++_tempIdCounter}`;

const ManifestReceivingScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Session pallets -- all pallet data lives in component state until "Complete Receiving"
  const [sessionPallets, setSessionPallets] = useState([]);
  // Cache last-entered dims per SKU (line_id) so subsequent pallets
  // with the same SKU auto-fill. Keyed by line_id.
  const skuDimsCache = useRef({});

  // Pallet modal state
  const [palletModalOpen, setPalletModalOpen] = useState(false);
  const [editingPalletId, setEditingPalletId] = useState(null);
  const [palletFormData, setPalletFormData] = useState(null);

  // Print state
  const [isPrintingPalletLabels, setIsPrintingPalletLabels] = useState(false);

  // Hold/cancel workflow state
  const [isHoldModalOpen, setIsHoldModalOpen] = useState(false);
  const [selectedHoldLine, setSelectedHoldLine] = useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedCancelLine, setSelectedCancelLine] = useState(null);

  // Document upload state
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  // Fetch manifest details
  const {
    data: manifestData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['manifest-for-receiving', id],
    () => manifestReceivingAPI.getManifest(id),
    { refetchOnWindowFocus: false }
  );

  const manifest = manifestData?.manifest;
  const lineItems = manifest?.InboundProjectLines || [];
  const receivingPreferences = manifest?.receiving_instructions || manifest?.receivingPreferences || manifest?.receivingInstructions || {};
  const receivingServiceLabels = getReceivingServiceLabels(receivingPreferences);

  // Fetch existing documents for this manifest's request
  const { data: existingDocs, refetch: refetchDocs } = useQuery(
    ['manifest-documents', manifest?.request_id],
    () => manifest?.request_id ? documentsAPI.getDocuments(manifest.request_id) : Promise.resolve({ data: [] }),
    { enabled: !!manifest?.request_id, staleTime: 30000 }
  );

  useEffect(() => {
    if (existingDocs?.data) {
      setUploadedDocs(existingDocs.data);
    }
  }, [existingDocs]);

  // Compute remaining quantities for each line, accounting for session pallets
  const lineRemainingMap = useMemo(() => {
    const map = {};
    for (const line of lineItems) {
      const alreadyReceived = line.qty_received || 0;
      // Sum quantities in session pallets for this line
      let sessionQty = 0;
      for (const pallet of sessionPallets) {
        for (const content of pallet.contents) {
          if (content.line_id === line.id) {
            sessionQty += parseInt(content.quantity) || 0;
          }
        }
      }
      map[line.id] = Math.max(0, (line.qty_expected || 0) - alreadyReceived - sessionQty);
    }
    return map;
  }, [lineItems, sessionPallets]);

  // Compute session quantities per line
  const sessionQtyMap = useMemo(() => {
    const map = {};
    for (const pallet of sessionPallets) {
      for (const content of pallet.contents) {
        map[content.line_id] = (map[content.line_id] || 0) + (parseInt(content.quantity) || 0);
      }
    }
    return map;
  }, [sessionPallets]);

  // Receive pallets mutation
  const receivePalletsMutation = useMutation(
    (data) => manifestReceivingAPI.receivePallets(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['manifest-for-receiving', id]);
        queryClient.invalidateQueries(['manifest-detail', id]);
        toast.success('Pallets received successfully');
        setSessionPallets([]);
      },
      onError: (error) => {
        toast.error('Failed to receive pallets: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  // Hold line mutation
  const holdLineMutation = useMutation(
    ({ lineId, holdReason, holdQuantity }) =>
      manifestReceivingAPI.holdManifestLine(id, lineId, { holdReason, holdQuantity }),
    {
      onSuccess: () => {
        toast.info('Successfully placed item on hold');
        queryClient.invalidateQueries(['manifest-for-receiving', id]);
        queryClient.invalidateQueries(['manifest-detail', id]);
        setIsHoldModalOpen(false);
        setSelectedHoldLine(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Error placing item on hold');
      }
    }
  );

  // Cancel held items mutation
  const cancelHeldMutation = useMutation(
    ({ lineId, cancelReason, cancelQuantity }) =>
      manifestReceivingAPI.cancelHeldManifestLine(id, lineId, { cancelReason, cancelQuantity }),
    {
      onSuccess: () => {
        toast.success('Successfully cancelled held items');
        queryClient.invalidateQueries(['manifest-for-receiving', id]);
        queryClient.invalidateQueries(['manifest-detail', id]);
        setIsCancelModalOpen(false);
        setSelectedCancelLine(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Error cancelling held items');
      }
    }
  );

  // Receive from hold mutation
  const receiveFromHoldMutation = useMutation(
    ({ lineId, quantity, binLocation }) =>
      manifestReceivingAPI.receiveFromHoldManifestLine(id, lineId, {
        quantity,
        binLocation,
        warehouseId: manifest?.destination_warehouse_id
      }),
    {
      onSuccess: () => {
        toast.success('Successfully received items from hold');
        queryClient.invalidateQueries(['manifest-for-receiving', id]);
        queryClient.invalidateQueries(['manifest-detail', id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Error receiving items from hold');
      }
    }
  );

  // --- Hold/cancel handlers ---
  const handleOpenHoldModal = (line) => {
    setSelectedHoldLine({
      id: line.id,
      productSku: line.original_sku || line.sku,
      productName: line.item_name || line.description || 'No description',
      qtyOrdered: line.qty_expected,
      qtyReceived: line.qty_received || 0,
      qtyOnHold: line.qty_on_hold || 0
    });
    setIsHoldModalOpen(true);
  };

  const handleConfirmHold = (holdData) => {
    holdLineMutation.mutate({
      lineId: selectedHoldLine.id,
      holdReason: holdData.holdReason,
      holdQuantity: holdData.holdQuantity,
      serialNumbers: holdData.serialNumbers
    });
  };

  const handleOpenCancelModal = (line) => {
    setSelectedCancelLine({
      id: line.id,
      productSku: line.original_sku || line.sku,
      productName: line.item_name || line.description || 'No description',
      qtyOnHold: line.qty_on_hold || 0
    });
    setIsCancelModalOpen(true);
  };

  const handleConfirmCancel = (cancelReason, quantityToCancel) => {
    cancelHeldMutation.mutate({
      lineId: selectedCancelLine.id,
      cancelReason,
      cancelQuantity: quantityToCancel
    });
  };

  const handleReceiveFromHold = (line, binLocation) => {
    if (!binLocation?.trim()) {
      toast.error('Bin location is required');
      return;
    }
    receiveFromHoldMutation.mutate({
      lineId: line.id,
      quantity: line.qty_on_hold || 0,
      binLocation: binLocation.trim()
    });
  };

  // --- Pallet modal handlers ---
  const openAddPalletModal = (preSelectedLineId) => {
    const defaultContents = [];
    if (preSelectedLineId) {
      defaultContents.push({
        id: generateTempId(),
        line_id: preSelectedLineId,
        quantity: '',
        serial_numbers: [],
        mac_addresses: []
      });
    }
    setPalletFormData({
      id: generateTempId(),
      label: '',
      weight_lbs: '',
      length: '',
      width: '',
      height: '',
      notes: '',
      contents: defaultContents
    });
    setEditingPalletId(null);
    setPalletModalOpen(true);
  };

  const openEditPalletModal = (pallet) => {
    setPalletFormData(JSON.parse(JSON.stringify(pallet)));
    setEditingPalletId(pallet.id);
    setPalletModalOpen(true);
  };

  const handleDuplicatePallet = (pallet) => {
    const duplicate = JSON.parse(JSON.stringify(pallet));
    duplicate.id = generateTempId();
    duplicate.label = '';
    // Give each content row a new temp id
    duplicate.contents = duplicate.contents.map(c => ({
      ...c,
      id: generateTempId(),
      serial_numbers: [],
      mac_addresses: []
    }));
    setSessionPallets(prev => [...prev, duplicate]);
    toast.success('Pallet duplicated');
  };

  const handleDeletePallet = (palletId) => {
    setSessionPallets(prev => prev.filter(p => p.id !== palletId));
  };

  const handleSavePallet = (palletData) => {
    // Cache dims per SKU so the next pallet with the same SKU auto-fills
    for (const c of palletData.contents || []) {
      if (!c.line_id) continue;
      const dims = {};
      for (const f of ['pallet_contents_type', 'item_length', 'item_width', 'item_height', 'item_weight_lbs',
        'carton_length', 'carton_width', 'carton_height', 'carton_weight', 'qty_per_carton']) {
        if (c[f] !== '' && c[f] != null) dims[f] = c[f];
      }
      if (Object.keys(dims).length > 0) {
        skuDimsCache.current[c.line_id] = dims;
      }
    }

    if (editingPalletId) {
      setSessionPallets(prev => prev.map(p => p.id === editingPalletId ? palletData : p));
    } else {
      setSessionPallets(prev => [...prev, palletData]);
    }
    setPalletModalOpen(false);
    setPalletFormData(null);
    setEditingPalletId(null);
  };

  // --- Complete receiving ---
  const handleCompleteReceiving = () => {
    if (sessionPallets.length === 0) return;

    // Validate all pallets have at least one content row with quantity
    for (const pallet of sessionPallets) {
      if (!pallet.contents || pallet.contents.length === 0) {
        toast.error('Each pallet must have at least one SKU');
        return;
      }
      for (const content of pallet.contents) {
        if (!content.line_id || !content.quantity || parseInt(content.quantity) <= 0) {
          toast.error('Each content row must have a SKU selected and a quantity > 0');
          return;
        }
        // Validate serial numbers if required
        const qty = parseInt(content.quantity);
        if (receivingPreferences.require_serial_numbers_on_receive) {
          const serials = (content.serial_numbers || []).filter(s => s?.trim());
          if (serials.length !== qty) {
            const line = lineItems.find(l => l.id === content.line_id);
            toast.error(`Serial numbers required for all ${qty} units of ${line?.original_sku || line?.sku || 'item'}`);
            return;
          }
        }
        if (receivingPreferences.require_mac_addresses_on_receive) {
          const macs = (content.mac_addresses || []).filter(m => m?.trim());
          if (macs.length !== qty) {
            const line = lineItems.find(l => l.id === content.line_id);
            toast.error(`MAC addresses required for all ${qty} units of ${line?.original_sku || line?.sku || 'item'}`);
            return;
          }
        }
      }
    }

    const payload = {
      warehouse_id: manifest.destination_warehouse_id,
      pallets: sessionPallets.map(p => ({
        label: p.label || null,
        weight_lbs: p.weight_lbs ? parseFloat(p.weight_lbs) : null,
        length: p.length ? parseFloat(p.length) : null,
        width: p.width ? parseFloat(p.width) : null,
        height: p.height ? parseFloat(p.height) : null,
        notes: p.notes || null,
        contents: p.contents.map(c => ({
          line_id: c.line_id,
          quantity: parseInt(c.quantity),
          serial_numbers: (c.serial_numbers || []).filter(s => s?.trim()),
          mac_addresses: (c.mac_addresses || []).filter(m => m?.trim()),
          // Per-SKU item and carton dimensions (optional, written to
          // InventoryItem only if the item doesn't already have dims)
          item_length: c.item_length ? parseFloat(c.item_length) : null,
          item_width: c.item_width ? parseFloat(c.item_width) : null,
          item_height: c.item_height ? parseFloat(c.item_height) : null,
          item_weight_lbs: c.item_weight_lbs ? parseFloat(c.item_weight_lbs) : null,
          carton_length: c.carton_length ? parseFloat(c.carton_length) : null,
          carton_width: c.carton_width ? parseFloat(c.carton_width) : null,
          carton_height: c.carton_height ? parseFloat(c.carton_height) : null,
          carton_weight: c.carton_weight ? parseFloat(c.carton_weight) : null,
          qty_per_carton: c.qty_per_carton ? parseInt(c.qty_per_carton) : null,
          pallet_contents_type: (c.carton_length || c.carton_width || c.carton_height) ? 'carton' : 'individual'
        }))
      }))
    };

    receivePalletsMutation.mutate(payload);
  };

  // --- Print pallet labels ---
  const handlePrintPalletLabels = async () => {
    if (!manifest?.request_id) {
      toast.error('No request linked to this manifest.');
      return;
    }
    setIsPrintingPalletLabels(true);
    try {
      const html = await adminAPI.getPalletLabels(manifest.request_id);
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
        win.focus();
        setTimeout(() => win.print(), 400);
      } else {
        toast.error('Pop-up blocked. Please allow pop-ups for this site.');
      }
    } catch (err) {
      const message = err.response?.status === 404
        ? 'No pallets found for this manifest. Receive items first to create pallets.'
        : (err.response?.data || 'Failed to load pallet labels');
      toast.error(message);
    } finally {
      setIsPrintingPalletLabels(false);
    }
  };

  // --- Document handlers ---
  const handleDocumentUpload = async (files) => {
    if (!manifest?.request_id) {
      toast.error('No request linked to this manifest. Cannot upload documents.');
      return;
    }
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" exceeds the 10 MB file size limit.`);
        continue;
      }
      try {
        const ext = file.name.split('.').pop().toLowerCase();
        const typeMap = { pdf: 'bol', jpg: 'photo', jpeg: 'photo', png: 'photo', gif: 'photo' };
        const documentType = typeMap[ext] || 'other';
        await documentsAPI.uploadDocument(manifest.request_id, file, documentType, file.name);
        toast.success(`"${file.name}" uploaded`);
      } catch (error) {
        toast.error(`Failed to upload "${file.name}"`);
      }
    }
    refetchDocs();
  };

  const handleDeleteDocument = async (documentId) => {
    if (!manifest?.request_id) return;
    try {
      await documentsAPI.deleteDocument(manifest.request_id, documentId);
      toast.success('Document removed');
      refetchDocs();
    } catch (error) {
      toast.error('Failed to remove document');
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) handleDocumentUpload(e.dataTransfer.files);
  };

  // --- Rendering ---
  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message={error.message || 'Error loading manifest'} />;
  if (!manifest) return <ErrorAlert message="Manifest not found or not ready for receiving" />;

  const totalExpected = manifest.totalQtyExpected || lineItems.reduce((s, l) => s + (l.qty_expected || 0), 0);
  const totalReceived = manifest.totalQtyReceived || lineItems.reduce((s, l) => s + (l.qty_received || 0), 0);
  const totalInSession = Object.values(sessionQtyMap).reduce((s, v) => s + v, 0);
  const totalRemaining = Math.max(0, totalExpected - totalReceived - totalInSession);
  const linesWithHold = lineItems.filter(item => (item.qty_on_hold || 0) > 0);
  const isFullyReceived = manifest.status === 'received';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/admin/inbound-projects/${id}`)}
            className="text-gray-600 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-100"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Receiving: {manifest.name}</h1>
            <p className="text-gray-600">
              {manifest.project_code} {manifest.Company?.companyName ? `-- ${manifest.Company.companyName}` : ''} {manifest.Warehouse?.name ? `-- ${manifest.Warehouse.name}` : ''} -- Status: {manifest.status}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {manifest?.request_id && (
            <button
              type="button"
              onClick={handlePrintPalletLabels}
              disabled={isPrintingPalletLabels}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <FiPrinter className="mr-2 h-4 w-4" />
              {isPrintingPalletLabels ? 'Loading...' : 'Print Pallet Labels'}
            </button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Total Expected</p>
              <p className="text-2xl font-semibold text-gray-900">{totalExpected}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Received</p>
              <p className="text-2xl font-semibold text-green-600">{totalReceived}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">In Session</p>
              <p className="text-2xl font-semibold text-blue-600">{totalInSession}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Remaining</p>
              <p className="text-2xl font-semibold text-amber-600">{totalRemaining}</p>
            </div>
          </div>
          {receivingServiceLabels.length > 0 && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <p className="text-sm font-medium text-gray-700">Receiving Instructions</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {receivingServiceLabels.map((label) => (
                  <span key={label} className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )}
          {linesWithHold.length > 0 && (
            <div className="mt-3 text-sm text-red-600 font-medium">
              {linesWithHold.reduce((sum, item) => sum + (item.qty_on_hold || 0), 0)} items on hold
            </div>
          )}
        </div>
      </div>

      {/* Manifest Documents */}
      <div className="bg-white rounded-lg shadow border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Manifest Documents (BOL, Packing Lists)
        </h3>
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-1">Drag and drop files here, or click to browse</p>
          <p className="text-xs text-gray-400 mb-3">Max 10 MB per file. PDF, images, Excel, CSV accepted.</p>
          <input
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            onChange={(e) => { handleDocumentUpload(e.target.files); e.target.value = ''; }}
            className="hidden"
            id="manifest-documents"
          />
          <label
            htmlFor="manifest-documents"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer"
          >
            <FiUpload className="w-4 h-4 mr-2" />
            Choose Files
          </label>
        </div>

        {uploadedDocs.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Uploaded Documents:</p>
            <div className="space-y-2">
              {uploadedDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2 min-w-0">
                    <span className="text-sm text-gray-900 truncate">{doc.original_name}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB` : ''}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <button
                      onClick={async () => {
                        try {
                          const urlRes = await documentsAPI.getDocumentUrl(manifest.request_id, doc.id);
                          window.open(urlRes.url, '_blank', 'noopener,noreferrer');
                        } catch (err) {
                          toast.error('Failed to open document');
                        }
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDeleteDocument(doc.id)}
                      className="text-red-600 hover:text-red-800"
                      aria-label={`Remove ${doc.original_name}`}
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Section 1: Expected Items (read-only table) */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Expected Items</h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Expected</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Received</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">In Session</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining</th>
              {linesWithHold.length > 0 && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Hold</th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {lineItems.map(line => {
              const received = line.qty_received || 0;
              const inSession = sessionQtyMap[line.id] || 0;
              const remaining = lineRemainingMap[line.id] || 0;
              const expected = line.qty_expected || 0;
              const isComplete = remaining === 0 && inSession === 0 && received >= expected;
              const isPartial = received > 0 || inSession > 0;
              const hasHold = (line.qty_on_hold || 0) > 0;

              let rowClass = '';
              if (isComplete) rowClass = 'bg-green-50';
              else if (isPartial) rowClass = 'bg-amber-50';

              return (
                <tr key={line.id} className={rowClass}>
                  <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {line.original_sku || line.sku}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                    {line.item_name || line.description || '--'}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                    {line.project_name || line.project_code || '--'}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">{expected}</td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-green-600 font-medium text-right">{received}</td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-blue-600 font-medium text-right">{inSession}</td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-right">
                    <span className={remaining > 0 ? 'text-amber-600 font-medium' : 'text-gray-400'}>{remaining}</span>
                  </td>
                  {linesWithHold.length > 0 && (
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-right">
                      {hasHold ? (
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-red-600 font-medium">{line.qty_on_hold}</span>
                          {line.hold_reason && (
                            <HoldInfoPopover
                              holdReason={line.hold_reason}
                              holdDate={line.hold_date}
                              holdQuantity={line.qty_on_hold}
                              totalQuantity={line.qty_expected}
                              holdBy={line.hold_by}
                            />
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">--</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Hold actions for lines that have items on hold */}
      {linesWithHold.length > 0 && (
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Items on Hold</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {linesWithHold.map(line => (
              <HoldLineActions
                key={line.id}
                line={line}
                onHold={handleOpenHoldModal}
                onCancel={handleOpenCancelModal}
                onReceiveFromHold={handleReceiveFromHold}
                isReceiveFromHoldLoading={receiveFromHoldMutation.isLoading && receiveFromHoldMutation.variables?.lineId === line.id}
                isCancelLoading={cancelHeldMutation.isLoading && cancelHeldMutation.variables?.lineId === line.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Section 2: Receiving Pallets */}
      {!isFullyReceived && (
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Receiving Pallets
              {sessionPallets.length > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500">({sessionPallets.length} pallet{sessionPallets.length !== 1 ? 's' : ''} in session)</span>
              )}
            </h3>
            <div className="flex gap-2">
              {/* Quick Add: opens modal with the first available SKU pre-selected */}
              {lineItems.some(l => (lineRemainingMap[l.id] || 0) > 0) && sessionPallets.length > 0 && (
                <button
                  onClick={() => {
                    const firstAvail = lineItems.find(l => (lineRemainingMap[l.id] || 0) > 0);
                    if (firstAvail) openAddPalletModal(firstAvail.id);
                  }}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <FiPlus className="w-4 h-4 mr-1" />
                  Quick Add (Single SKU)
                </button>
              )}
            </div>
          </div>

          <div className="p-6">
            {sessionPallets.length === 0 ? (
              <div className="text-center py-8">
                <FiPackage className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-4">No pallets in this receiving session yet.</p>
                <button
                  onClick={() => openAddPalletModal(null)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                  <FiPlus className="w-4 h-4 mr-2" />
                  Start Receiving
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {sessionPallets.map((pallet) => {
                  const totalUnits = pallet.contents.reduce((s, c) => s + (parseInt(c.quantity) || 0), 0);
                  return (
                    <div key={pallet.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                            <FiPackage className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {pallet.label || 'Auto-generated label'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {pallet.contents.length} SKU{pallet.contents.length !== 1 ? 's' : ''} -- {totalUnits} unit{totalUnits !== 1 ? 's' : ''}
                              {pallet.weight_lbs ? ` -- ${pallet.weight_lbs} lbs` : ''}
                              {pallet.length && pallet.width && pallet.height
                                ? ` -- ${pallet.length}" x ${pallet.width}" x ${pallet.height}"`
                                : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditPalletModal(pallet)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                            title="Edit pallet"
                          >
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDuplicatePallet(pallet)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                            title="Duplicate pallet"
                          >
                            <FiCopy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePallet(pallet.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
                            title="Delete pallet"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {/* Show contents summary */}
                      <div className="mt-2 pl-14">
                        {pallet.contents.map(c => {
                          const line = lineItems.find(l => l.id === c.line_id);
                          return (
                            <div key={c.id} className="text-xs text-gray-500">
                              {line ? (line.original_sku || line.sku) : 'Unknown'}: {c.quantity} unit{parseInt(c.quantity) !== 1 ? 's' : ''}
                              {(c.serial_numbers || []).filter(s => s?.trim()).length > 0 && ` (${c.serial_numbers.filter(s => s?.trim()).length} serials)`}
                              {(c.mac_addresses || []).filter(m => m?.trim()).length > 0 && ` (${c.mac_addresses.filter(m => m?.trim()).length} MACs)`}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                <button
                  onClick={() => openAddPalletModal(null)}
                  className="inline-flex items-center px-3 py-2 border border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:text-gray-800 hover:border-gray-400 w-full justify-center"
                >
                  <FiPlus className="w-4 h-4 mr-2" />
                  Add Pallet
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section 3: Complete Session */}
      {!isFullyReceived && sessionPallets.length > 0 && (
        <div className="bg-white rounded-lg shadow border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Complete Receiving</h3>
              <p className="text-sm text-gray-500 mt-1">
                Submit {sessionPallets.length} pallet{sessionPallets.length !== 1 ? 's' : ''} with {totalInSession} total units to the warehouse.
              </p>
            </div>
            <button
              onClick={handleCompleteReceiving}
              disabled={receivePalletsMutation.isLoading}
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiCheck className="w-4 h-4 mr-2" />
              {receivePalletsMutation.isLoading ? 'Processing...' : 'Complete Receiving'}
            </button>
          </div>
        </div>
      )}

      {/* Fully received read-only state */}
      {isFullyReceived && (
        <div className="bg-green-50 rounded-lg shadow border border-green-200 p-8 text-center">
          <FiCheck className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-green-800 mb-1">All Items Received</h3>
          <p className="text-green-600">This manifest has been fully received.</p>
        </div>
      )}

      {/* Hold Line Item Modal */}
      <HoldLineItemModal
        isOpen={isHoldModalOpen}
        lineItem={selectedHoldLine}
        onClose={() => { setIsHoldModalOpen(false); setSelectedHoldLine(null); }}
        onConfirmHold={handleConfirmHold}
        isLoading={holdLineMutation.isLoading}
      />

      {/* Cancel Line Item Modal */}
      <CancelLineItemModal
        isOpen={isCancelModalOpen}
        lineItem={selectedCancelLine}
        onClose={() => { setIsCancelModalOpen(false); setSelectedCancelLine(null); }}
        onConfirmCancel={handleConfirmCancel}
        isLoading={cancelHeldMutation.isLoading}
      />

      {/* Pallet Modal */}
      {palletModalOpen && palletFormData && (
        <PalletModal
          isOpen={palletModalOpen}
          palletData={palletFormData}
          isEditing={!!editingPalletId}
          lineItems={lineItems}
          lineRemainingMap={lineRemainingMap}
          sessionPallets={sessionPallets}
          editingPalletId={editingPalletId}
          receivingPreferences={receivingPreferences}
          skuDimsCache={skuDimsCache}
          onSave={handleSavePallet}
          onClose={() => { setPalletModalOpen(false); setPalletFormData(null); setEditingPalletId(null); }}
        />
      )}
    </div>
  );
};

// Hold actions row for lines with items on hold
const HoldLineActions = ({ line, onHold, onCancel, onReceiveFromHold, isReceiveFromHoldLoading, isCancelLoading }) => {
  const [binLocation, setBinLocation] = useState('');
  const hasOnHold = (line.qty_on_hold || 0) > 0;
  const hasPending = (line.qty_pending || 0) > 0 || ((line.qty_expected - (line.qty_received || 0) - (line.qty_on_hold || 0)) > 0);

  return (
    <div className="px-6 py-4 flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-900">{line.original_sku || line.sku}</p>
        <p className="text-sm text-gray-500">{line.item_name || line.description || '--'}</p>
        <p className="text-xs text-red-600 font-medium mt-1">{line.qty_on_hold} on hold</p>
      </div>
      <div className="flex items-center gap-2">
        {hasPending && (
          <button
            onClick={() => onHold(line)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700"
          >
            <FiPause className="w-3.5 h-3.5 mr-1" />
            Hold
          </button>
        )}
        {hasOnHold && (
          <>
            <input
              type="text"
              value={binLocation}
              onChange={(e) => setBinLocation(e.target.value)}
              className="w-28 border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm"
              placeholder="Bin Location"
            />
            <button
              onClick={() => onReceiveFromHold(line, binLocation)}
              disabled={isReceiveFromHoldLoading}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              <FiCheck className="w-3.5 h-3.5 mr-1" />
              {isReceiveFromHoldLoading ? '...' : 'Receive from Hold'}
            </button>
            <button
              onClick={() => onCancel(line)}
              disabled={isCancelLoading}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 disabled:opacity-50"
            >
              <FiSlash className="w-3.5 h-3.5 mr-1" />
              {isCancelLoading ? '...' : 'Cancel'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Pallet Modal -- add/edit a pallet in the session
const PalletModal = ({
  isOpen,
  palletData: initialData,
  isEditing,
  lineItems,
  lineRemainingMap,
  sessionPallets,
  editingPalletId,
  receivingPreferences,
  skuDimsCache,
  onSave,
  onClose
}) => {
  const [data, setData] = useState(initialData);

  if (!isOpen) return null;

  const handleFieldChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const handleContentChange = (contentId, field, value) => {
    setData(prev => ({
      ...prev,
      contents: prev.contents.map(c =>
        c.id === contentId ? { ...c, [field]: value } : c
      )
    }));
  };

  const addContentRow = () => {
    setData(prev => ({
      ...prev,
      contents: [
        ...prev.contents,
        {
          id: generateTempId(),
          line_id: '',
          quantity: '',
          serial_numbers: [],
          mac_addresses: [],
          pallet_contents_type: 'individual',
          show_item_dims: false,
          item_length: '', item_width: '', item_height: '', item_weight_lbs: '',
          carton_length: '', carton_width: '', carton_height: '', carton_weight: '',
          qty_per_carton: '', carton_uom: 'Each'
        }
      ]
    }));
  };

  const removeContentRow = (contentId) => {
    setData(prev => ({
      ...prev,
      contents: prev.contents.filter(c => c.id !== contentId)
    }));
  };

  // Compute remaining per line, excluding the current pallet being edited
  const getMaxForLine = (lineId, currentContentId) => {
    const baseRemaining = lineRemainingMap[lineId] || 0;
    // If we are editing, the lineRemainingMap already subtracted session pallet quantities
    // including the pallet we are editing. Add back what this pallet's OTHER content rows
    // (already saved in session) contributed for this line.
    let addBack = 0;
    if (editingPalletId) {
      const originalPallet = sessionPallets.find(p => p.id === editingPalletId);
      if (originalPallet) {
        for (const c of originalPallet.contents) {
          if (c.line_id === lineId) {
            addBack += parseInt(c.quantity) || 0;
          }
        }
      }
    }
    // Subtract what other content rows in THIS modal form are using for this line
    let usedInForm = 0;
    for (const c of data.contents) {
      if (c.id !== currentContentId && c.line_id === lineId) {
        usedInForm += parseInt(c.quantity) || 0;
      }
    }
    return Math.max(0, baseRemaining + addBack - usedInForm);
  };

  // Available line items for a given content row (only lines with remaining > 0, or the currently selected one)
  const getAvailableLines = (currentContentId, currentLineId) => {
    return lineItems.filter(line => {
      if (line.id === currentLineId) return true;
      return getMaxForLine(line.id, currentContentId) > 0;
    });
  };

  const handleSave = () => {
    // Validate
    const validContents = data.contents.filter(c => c.line_id && parseInt(c.quantity) > 0);
    if (validContents.length === 0) {
      toast.error('Add at least one SKU with a quantity to this pallet');
      return;
    }

    // Check quantities don't exceed remaining
    for (const c of validContents) {
      const max = getMaxForLine(c.line_id, c.id);
      if (parseInt(c.quantity) > max) {
        const line = lineItems.find(l => l.id === c.line_id);
        toast.error(`Quantity for ${line?.original_sku || line?.sku || 'item'} exceeds remaining (max ${max})`);
        return;
      }
    }

    // Validate serial/MAC counts
    for (const c of validContents) {
      const qty = parseInt(c.quantity);
      if (receivingPreferences.require_serial_numbers_on_receive) {
        const serials = (c.serial_numbers || []).filter(s => s?.trim());
        if (serials.length !== qty) {
          const line = lineItems.find(l => l.id === c.line_id);
          toast.error(`${line?.original_sku || line?.sku}: need ${qty} serial number${qty !== 1 ? 's' : ''}, have ${serials.length}`);
          return;
        }
      }
      if (receivingPreferences.require_mac_addresses_on_receive) {
        const macs = (c.mac_addresses || []).filter(m => m?.trim());
        if (macs.length !== qty) {
          const line = lineItems.find(l => l.id === c.line_id);
          toast.error(`${line?.original_sku || line?.sku}: need ${qty} MAC address${qty !== 1 ? 'es' : ''}, have ${macs.length}`);
          return;
        }
      }
    }

    onSave({ ...data, contents: validContents });
  };

  const requireSerials = receivingPreferences.require_serial_numbers_on_receive;
  const requireMacs = receivingPreferences.require_mac_addresses_on_receive;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                {isEditing ? 'Edit Pallet' : 'Add Pallet'}
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <FiX className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="bg-white px-6 py-6 max-h-[70vh] overflow-y-auto space-y-6">
            {/* Pallet metadata */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-800">Pallet Details</h4>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  value={data.label}
                  onChange={(e) => handleFieldChange('label', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Auto-generated if blank"
                />
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Weight (lbs)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={data.weight_lbs}
                    onChange={(e) => handleFieldChange('weight_lbs', e.target.value)}
                    className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Length (in)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={data.length}
                    onChange={(e) => handleFieldChange('length', e.target.value)}
                    className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Width (in)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={data.width}
                    onChange={(e) => handleFieldChange('width', e.target.value)}
                    className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Height (in)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={data.height}
                    onChange={(e) => handleFieldChange('height', e.target.value)}
                    className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  rows={2}
                  value={data.notes}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Optional receiving notes..."
                />
              </div>
            </div>

            {/* Pallet contents */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-800">Items on this Pallet</h4>

              {data.contents.length === 0 && (
                <p className="text-sm text-gray-500">No items added yet. Click "+ Add SKU" below.</p>
              )}

              {data.contents.map((content) => {
                const availableLines = getAvailableLines(content.id, content.line_id);
                const maxQty = content.line_id ? getMaxForLine(content.line_id, content.id) : 0;
                const qty = parseInt(content.quantity) || 0;

                return (
                  <div key={content.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">SKU</label>
                          <select
                            value={content.line_id || ''}
                            onChange={(e) => {
                              const selectedLineId = e.target.value || '';
                              handleContentChange(content.id, 'line_id', selectedLineId);
                              // Reset quantity and identifiers when SKU changes
                              handleContentChange(content.id, 'quantity', '');
                              handleContentChange(content.id, 'serial_numbers', []);
                              handleContentChange(content.id, 'mac_addresses', []);
                              // Auto-fill dims: prefer session cache (user's last
                              // entry for this SKU) over DB item dims. This way if
                              // the user enters dims on pallet 1, pallet 2 with the
                              // same SKU auto-fills without re-entry.
                              const cached = skuDimsCache.current[selectedLineId];
                              const selectedLine = lineItems.find(l => l.id === selectedLineId);
                              const item = selectedLine?.MatchedInventoryItem || {};
                              const src = cached || {};
                              const contentsType = src.pallet_contents_type
                                || (item.carton_length ? 'carton' : 'individual');
                              handleContentChange(content.id, 'pallet_contents_type', contentsType);
                              handleContentChange(content.id, 'item_length', src.item_length ?? item.length ?? '');
                              handleContentChange(content.id, 'item_width', src.item_width ?? item.width ?? '');
                              handleContentChange(content.id, 'item_height', src.item_height ?? item.height ?? '');
                              handleContentChange(content.id, 'item_weight_lbs', src.item_weight_lbs ?? item.weight_lbs ?? '');
                              handleContentChange(content.id, 'carton_length', src.carton_length ?? item.carton_length ?? '');
                              handleContentChange(content.id, 'carton_width', src.carton_width ?? item.carton_width ?? '');
                              handleContentChange(content.id, 'carton_height', src.carton_height ?? item.carton_height ?? '');
                              handleContentChange(content.id, 'carton_weight', src.carton_weight ?? item.carton_weight ?? '');
                              handleContentChange(content.id, 'qty_per_carton', src.qty_per_carton ?? item.units_per_carton ?? '');
                            }}
                            className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select SKU...</option>
                            {availableLines.map(line => (
                              <option key={line.id} value={line.id}>
                                {line.original_sku || line.sku} - {line.item_name || line.description || 'No name'}
                                {line.project_name ? ` [${line.project_name}]` : ''}
                                {line.project_code && !line.project_name ? ` [${line.project_code}]` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Quantity {content.line_id ? `(max ${maxQty})` : ''}
                          </label>
                          <input
                            type="number"
                            min="1"
                            max={maxQty}
                            value={content.quantity}
                            onChange={(e) => handleContentChange(content.id, 'quantity', e.target.value)}
                            className="w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                            disabled={!content.line_id}
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => removeContentRow(content.id)}
                        className="mt-5 p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
                        title="Remove"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Packaging type + dimensions (shown once SKU selected) */}
                    {content.line_id && (
                      <div className="mt-2 space-y-3">
                        {/* Issue 2: Force selection of individual or carton */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Items on this pallet are</label>
                          <div className="flex gap-3">
                            {[['individual', 'Individual Units'], ['carton', 'Cartons']].map(([val, label]) => (
                              <label key={val} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                <input
                                  type="radio"
                                  name={`contents_type_${content.id}`}
                                  value={val}
                                  checked={content.pallet_contents_type === val}
                                  onChange={() => handleContentChange(content.id, 'pallet_contents_type', val)}
                                  className="text-blue-600 focus:ring-blue-500"
                                />
                                {label}
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Individual unit dims — shown when type is 'individual' */}
                        {content.pallet_contents_type === 'individual' && (
                          <div className="bg-gray-50 rounded-md p-3">
                            <p className="text-xs font-medium text-gray-700 mb-2">Unit Dimensions</p>
                            <div className="grid grid-cols-4 gap-2">
                              {[['item_length', 'Length (in)'], ['item_width', 'Width (in)'], ['item_height', 'Height (in)'], ['item_weight_lbs', 'Weight (lbs)']].map(([field, label]) => (
                                <div key={field}>
                                  <label className="block text-xs text-gray-500">{label}</label>
                                  <input type="number" step="0.01" value={content[field] ?? ''}
                                    onChange={(e) => handleContentChange(content.id, field, e.target.value === '' ? '' : parseFloat(e.target.value))}
                                    className="w-full border-gray-300 rounded-md shadow-sm text-sm" placeholder="0" />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Carton dims + UOM on own line — shown when type is 'carton' */}
                        {content.pallet_contents_type === 'carton' && (
                          <div className="bg-blue-50 rounded-md p-3 space-y-2">
                            <p className="text-xs font-medium text-blue-900 mb-2">Carton Dimensions</p>
                            <div className="grid grid-cols-4 gap-2">
                              {[['carton_length', 'Length (in)'], ['carton_width', 'Width (in)'], ['carton_height', 'Height (in)'], ['carton_weight', 'Weight (lbs)']].map(([field, label]) => (
                                <div key={field}>
                                  <label className="block text-xs text-blue-800">{label}</label>
                                  <input type="number" step="0.01" value={content[field] ?? ''}
                                    onChange={(e) => handleContentChange(content.id, field, e.target.value === '' ? '' : parseFloat(e.target.value))}
                                    className="w-full border-blue-200 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500" placeholder="0" />
                                </div>
                              ))}
                            </div>
                            {/* UOM / Qty per carton on own line */}
                            <div className="grid grid-cols-2 gap-2 pt-1">
                              <div>
                                <label className="block text-xs text-blue-800">Units per Carton</label>
                                <input type="number" min="1" value={content.qty_per_carton ?? ''}
                                  onChange={(e) => handleContentChange(content.id, 'qty_per_carton', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                                  className="w-full border-blue-200 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500" placeholder="1" />
                              </div>
                              <div>
                                <label className="block text-xs text-blue-800">Unit of Measurement</label>
                                <select value={content.carton_uom ?? 'Each'}
                                  onChange={(e) => handleContentChange(content.id, 'carton_uom', e.target.value)}
                                  className="w-full border-blue-200 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500">
                                  <option value="Each">Each</option>
                                  <option value="Case">Case</option>
                                  <option value="Pack">Pack</option>
                                </select>
                              </div>
                            </div>
                            {/* Optional per-unit dims inside carton */}
                            <button type="button"
                              onClick={() => handleContentChange(content.id, 'show_item_dims', !content.show_item_dims)}
                              className="text-xs text-blue-600 hover:text-blue-800 underline">
                              {content.show_item_dims ? 'Hide' : 'Add'} individual unit dimensions
                            </button>
                            {content.show_item_dims && (
                              <div className="bg-white rounded-md p-2">
                                <p className="text-xs font-medium text-gray-700 mb-1">Per-Unit Dimensions (inside carton)</p>
                                <div className="grid grid-cols-4 gap-2">
                                  {[['item_length', 'Length (in)'], ['item_width', 'Width (in)'], ['item_height', 'Height (in)'], ['item_weight_lbs', 'Weight (lbs)']].map(([field, label]) => (
                                    <div key={field}>
                                      <label className="block text-xs text-gray-500">{label}</label>
                                      <input type="number" step="0.01" value={content[field] ?? ''}
                                        onChange={(e) => handleContentChange(content.id, field, e.target.value === '' ? '' : parseFloat(e.target.value))}
                                        className="w-full border-gray-300 rounded-md shadow-sm text-sm" placeholder="0" />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Serial numbers — always available when SKU selected + qty > 0 */}
                    {content.line_id && qty > 0 && (
                      <div className="mt-2">
                        {!content.show_serials && !requireSerials && (content.serial_numbers || []).length === 0 ? (
                          <button type="button"
                            onClick={() => handleContentChange(content.id, 'show_serials', true)}
                            className="text-xs text-blue-600 hover:text-blue-800 underline">
                            + Add Serial Numbers
                          </button>
                        ) : (
                          <div>
                            <BulkIdentifierInput
                              label="Serial Numbers"
                              values={content.serial_numbers || []}
                              onChange={(vals) => handleContentChange(content.id, 'serial_numbers', vals)}
                              maxCount={qty}
                              placeholder="Type serial and press Enter"
                            />
                            {requireSerials && (
                              <p className="text-xs text-amber-600 mt-1">
                                Required: {(content.serial_numbers || []).filter(s => s?.trim()).length} / {qty}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* MAC addresses — always available when SKU selected + qty > 0 */}
                    {content.line_id && qty > 0 && (
                      <div className="mt-1">
                        {!content.show_macs && !requireMacs && (content.mac_addresses || []).length === 0 ? (
                          <button type="button"
                            onClick={() => handleContentChange(content.id, 'show_macs', true)}
                            className="text-xs text-blue-600 hover:text-blue-800 underline">
                            + Add MAC Addresses
                          </button>
                        ) : (
                          <div>
                            <BulkIdentifierInput
                              label="MAC Addresses"
                              values={content.mac_addresses || []}
                              onChange={(vals) => handleContentChange(content.id, 'mac_addresses', vals)}
                              maxCount={qty}
                              placeholder="Type MAC address and press Enter"
                            />
                            {requireMacs && (
                              <p className="text-xs text-amber-600 mt-1">
                                Required: {(content.mac_addresses || []).filter(m => m?.trim()).length} / {qty}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              <button
                onClick={addContentRow}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                <FiPlus className="w-4 h-4 mr-1" />
                Add SKU
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              {isEditing ? 'Update Pallet' : 'Save Pallet'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManifestReceivingScreen;
